import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Icon,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { getAccessToken } from '../auth-store'
import { getTenantSlug } from '../tenant-store'
import { useTenantSlug } from '../use-tenant'

type Olay = components['schemas']['AuditEventRead']

const SAYFA = 50

/**
 * Denetim logu — YES-115.
 *
 * Kayıtlar SİLİNEMEZ ve DÜZENLENEMEZ (YES-81): bu ekranda yalnızca okuma var
 * ve olmayacak. Değiştirilebilir bir denetim kaydı, denetim kaydı değildir.
 *
 * Sayfalama imleçle: sürekli büyüyen bir tabloda offset, yeni kayıt geldikçe
 * sayfaları kaydırır ve aynı satır iki kez görünür. Toplam sayı da
 * gösterilmiyor — bölümlenmiş tabloda `COUNT(*)` her istekte tüm bölümleri
 * tarar (sunucu bilerek döndürmüyor).
 */
export function Denetim() {
  const slug = useTenantSlug()
  const [tur, setTur] = useState('')
  const [islem, setIslem] = useState('')
  const [secili, setSecili] = useState<Olay | null>(null)
  const [iniyor, setIniyor] = useState(false)
  const toast = useToast()

  // Dışa aktarım (YES-173): ekrandaki süzgeçle aynı kayıtlar, son 90 gün.
  // Tarih aralığı zorunlu — sunucu "hepsini indir"i kabul etmiyor.
  async function indir() {
    setIniyor(true)
    try {
      const since = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString()
      const params = new URLSearchParams({ since })
      if (tur) params.set('resource_type', tur)
      if (islem) params.set('operation', islem)
      const yanit = await fetch(`/api/v1/audit/export?${params}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
          ...(getTenantSlug() ? { 'X-Tenant-Slug': getTenantSlug()! } : {}),
        },
      })
      if (!yanit.ok) {
        toast({ title: 'İndirilemedi', tone: 'error' })
        return
      }
      const adres = URL.createObjectURL(await yanit.blob())
      const baglanti = document.createElement('a')
      baglanti.href = adres
      baglanti.download = 'denetim.csv'
      baglanti.click()
      URL.revokeObjectURL(adres)
    } finally {
      setIniyor(false)
    }
  }

  const katalog = useQuery({
    queryKey: ['audit-catalog', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/audit/catalog')
      return data
    },
  })

  const kayitlar = useInfiniteQuery({
    queryKey: ['audit', slug, tur, islem],
    enabled: Boolean(slug),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.GET('/api/v1/audit', {
        params: {
          query: {
            limit: SAYFA,
            ...(tur ? { resource_type: tur } : {}),
            ...(islem ? { operation: islem as Olay['operation'] } : {}),
            ...(pageParam ? { before: pageParam } : {}),
          },
        },
      })
      return data!
    },
    getNextPageParam: (sonSayfa) => sonSayfa.next_before ?? undefined,
  })

  if (kayitlar.isError) {
    const yetkisiz = kayitlar.error instanceof ApiError && kayitlar.error.problem.status === 403
    return (
      <Sayfa>
        <Card>
          <CardBody className="text-sm">
            {yetkisiz
              ? 'Denetim kayıtlarını görme yetkiniz yok.'
              : 'Denetim kayıtları yüklenemedi.'}
          </CardBody>
        </Card>
      </Sayfa>
    )
  }

  const olaylar = kayitlar.data?.pages.flatMap((sayfa) => sayfa.items) ?? []

  return (
    <Sayfa>
      <Card>
        <CardHeader
          title="Denetim kayıtları"
          description="Kim, ne zaman, neyi değiştirdi. Kayıtlar silinemez ve düzenlenemez."
          action={
            <Button variant="secondary" size="sm" disabled={iniyor} onClick={() => void indir()}>
              {iniyor ? 'Hazırlanıyor…' : 'Son 90 günü CSV indir'}
            </Button>
          }
        />
        <CardBody className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Secim
              deger={tur}
              onDegis={setTur}
              bosEtiket="Tüm kayıt türleri"
              secenekler={katalog.data?.resource_types ?? []}
            />
            <Secim
              deger={islem}
              onDegis={setIslem}
              bosEtiket="Tüm işlemler"
              secenekler={katalog.data?.operations ?? []}
            />
            {(tur || islem) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTur('')
                  setIslem('')
                }}
              >
                Süzgeci temizle
              </Button>
            )}
          </div>

          {kayitlar.isLoading && <p className="text-sm text-[var(--ad-text-muted)]">Yükleniyor…</p>}

          {!kayitlar.isLoading && olaylar.length === 0 && (
            <EmptyState
              icon={<Icon name="list-checks" size={24} aria-hidden />}
              title="Kayıt yok"
              description={
                tur || islem
                  ? 'Bu süzgece uyan kayıt bulunamadı.'
                  : 'Derneğinizde henüz denetlenen bir işlem yapılmamış.'
              }
            />
          )}

          {olaylar.length > 0 && (
            <ul className="flex flex-col">
              {olaylar.map((olay) => (
                <li key={olay.id}>
                  <button
                    type="button"
                    onClick={() => setSecili(olay)}
                    className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-[var(--ad-border)] px-2 py-2 text-left text-sm last:border-b-0 hover:bg-[var(--ad-surface-muted)]"
                  >
                    <span className="w-36 shrink-0 text-xs tabular-nums text-[var(--ad-text-muted)]">
                      {zaman(olay.created_at)}
                    </span>
                    <span className="font-medium">{olay.actor_name ?? 'Sistem'}</span>
                    <span className="text-[var(--ad-text-muted)]">
                      {olay.operation_label.toLocaleLowerCase('tr')}
                    </span>
                    <span>{olay.resource_label}</span>

                    {/* Destek oturumu ayrı işaretleniyor: eylem derneğin
                        yöneticisi adına görünse de arkasındaki personel
                        kaybolmamalı (YES-86). */}
                    {olay.impersonator_name && (
                      <Badge tone="warning">Destek: {olay.impersonator_name}</Badge>
                    )}
                    {olay.operation === 'read' && <Badge tone="info">Görüntüleme</Badge>}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {kayitlar.hasNextPage && (
            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={kayitlar.isFetchingNextPage}
                onClick={() => void kayitlar.fetchNextPage()}
              >
                {kayitlar.isFetchingNextPage ? 'Yükleniyor…' : 'Daha eski kayıtlar'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {secili && <Ayrinti olay={secili} onKapat={() => setSecili(null)} />}
    </Sayfa>
  )
}

function Secim({
  deger,
  onDegis,
  bosEtiket,
  secenekler,
}: {
  deger: string
  onDegis: (deger: string) => void
  bosEtiket: string
  secenekler: readonly { value: string; label: string }[]
}) {
  return (
    <select
      value={deger}
      onChange={(event) => onDegis(event.target.value)}
      className="rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 py-1.5 text-sm"
    >
      <option value="">{bosEtiket}</option>
      {secenekler.map((secenek) => (
        <option key={secenek.value} value={secenek.value}>
          {secenek.label}
        </option>
      ))}
    </select>
  )
}

function Ayrinti({ olay, onKapat }: { olay: Olay; onKapat: () => void }) {
  const degisiklikler = Object.entries(olay.changes ?? {})

  // Diyalog, kartın altına açılan bir bölüm DEĞİL: elli satırlık listede
  // ayrıntı ekranın çok altında kalıyordu, tıklamak hiçbir şey yapmamış gibi
  // görünüyordu (tarayıcıda görüldü).
  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title={`${olay.resource_label} · ${olay.operation_label}`}
      description={`${olay.actor_name ?? 'Sistem'} · ${zaman(olay.created_at)}`}
    >
      <div className="flex flex-col gap-3">
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[8rem_1fr]">
          <dt className="text-[var(--ad-text-muted)]">Eylem</dt>
          <dd className="font-mono text-xs">{olay.action}</dd>
          {olay.resource_id && (
            <>
              <dt className="text-[var(--ad-text-muted)]">Kayıt</dt>
              <dd className="font-mono text-xs">{olay.resource_id}</dd>
            </>
          )}
          {olay.ip_address && (
            <>
              <dt className="text-[var(--ad-text-muted)]">IP adresi</dt>
              <dd className="font-mono text-xs">{olay.ip_address}</dd>
            </>
          )}
          {olay.impersonator_name && (
            <>
              <dt className="text-[var(--ad-text-muted)]">Destek personeli</dt>
              <dd>{olay.impersonator_name}</dd>
            </>
          )}
        </dl>

        {degisiklikler.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--ad-text-muted)]">
                  <th className="py-1 pr-3 font-medium">Alan</th>
                  <th className="py-1 pr-3 font-medium">Eski</th>
                  <th className="py-1 font-medium">Yeni</th>
                </tr>
              </thead>
              <tbody>
                {degisiklikler.map(([alan, deger]) => {
                  const { old: eski, new: yeni } = deger as { old: unknown; new: unknown }
                  return (
                    <tr key={alan} className="border-t border-[var(--ad-border)]">
                      {/* Alan adları ham kolon adları. Kısmî bir sözlük, hangi
                          alanın neden çevrilmediğini okunamaz hâle getirirdi. */}
                      <td className="py-1 pr-3 font-mono text-xs">{alan}</td>
                      <td className="py-1 pr-3 text-[var(--ad-text-muted)]">{yaz(eski)}</td>
                      <td className="py-1">{yaz(yeni)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-[var(--ad-text-muted)]">
            Bu olayda alan değişikliği yok — okuma ya da işlem kaydı.
          </p>
        )}
      </div>
    </Dialog>
  )
}

/** Sır alanları sunucuda zaten `***` ile maskeleniyor; burada yalnızca yazım. */
function yaz(deger: unknown): string {
  if (deger === null || deger === undefined) return '—'
  if (typeof deger === 'boolean') return deger ? 'evet' : 'hayır'
  return String(deger)
}

function zaman(iso: string): string {
  return new Date(iso).toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function Sayfa({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>
}
